import type { AxiosError } from "axios";
import axios from "axios";

import { useAuthStore } from "@app/auth/store";

// In dev the Vite proxy serves /api and /admin from the same origin, so the
// default baseURL is "/". Override with VITE_BACKEND_BASEURL for non-proxied setups.
export const api = axios.create({
  baseURL: import.meta.env.VITE_BACKEND_BASEURL || "/",
  timeout: 20_000
});

api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token;
  if (token) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err: AxiosError) => {
    // 401 = token invalid/expired, 403 = not a backoffice admin. Either way the
    // current session cannot use the backoffice, so drop it and force re-login.
    const status = err.response?.status;
    if (status === 401 || status === 403) {
      useAuthStore.getState().clear();
    }
    return Promise.reject(err);
  }
);

export function extractErrorMessage(err: unknown, fallback = "Unexpected error"): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { message?: string; error?: string } | undefined;
    return data?.message || data?.error || err.message || fallback;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

export interface ListResponse<T> {
  items: T[];
  offset: number;
  limit: number;
  total?: number;
}
